import React, { useState, useRef, useCallback } from "react";
import "./Contact.css";

export default function Contact({ id = "contact" }) {
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const formRef = useRef(null);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // 1) récupère un vrai élément <form> de manière sûre
    const formEl =
      formRef.current ||
      (e?.currentTarget ?? e?.target?.closest?.("form")) ||
      null;

    if (!formEl) {
      console.warn("[Contact] Aucun élément <form> trouvé pour reset()");
      return;
    }

    const fd = new FormData(formEl);

    // 2) honeypot
    if (fd.get("website")) return;

    // 3) payload
    const payload = {
      societe: fd.get("societe")?.trim(),
      code_postal: fd.get("code_postal")?.trim(),
      nom_prenom: fd.get("nom_prenom")?.trim(),
      email: fd.get("email")?.trim(),
      telephone: fd.get("telephone")?.trim(),
      message: fd.get("message")?.trim(),
      source: window.location.href,
      website: fd.get("website") || "",
    };

    if (!payload.societe || !payload.code_postal || !payload.nom_prenom || !payload.email) {
      setStatus({ state: "error", msg: "Veuillez remplir tous les champs requis." });
      return;
    }

    try {
      setStatus({ state: "loading", msg: "" });

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Échec de l’envoi.");

      // Si tu es en mode Ethereal, tu auras data.preview (URL d’aperçu)
      if (data?.preview) {
        console.log("Aperçu email (Ethereal):", data.preview);
      }

      setStatus({ state: "ok", msg: "Merci ! Votre message a été envoyé." });

      // 4) reset robuste (ne dépend pas de l’event)
      //   - on attend le prochain frame au cas où React recycle l’event
      requestAnimationFrame(() => {
        // désélectionne aussi la case captcha si besoin
        try {
          formEl.reset();
          const captcha = formEl.querySelector("#captcha");
          if (captcha) captcha.checked = false;
        } catch (err) {
          console.warn("[Contact] reset() a échoué:", err);
        }
      });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.message || "Une erreur est survenue. Merci de réessayer plus tard.",
      });
    }
  }, []);

  return (
    <section id={id} className="contact-wrapper">
      <h2 className="contact-title">Formulaire de contact</h2>

      <div className="contact-grid">
        {/* === FORMULAIRE === */}
        <form ref={formRef} className="contact-form" onSubmit={handleSubmit} noValidate>
          {/* Honeypot (caché via CSS) */}
          <label className="hp">
            Ne pas remplir
            <input name="website" type="text" tabIndex="-1" autoComplete="off" />
          </label>

          <div className="contact-row">
            <label>
              Société <span className="req">*</span>
              <input name="societe" type="text" placeholder="Société" required />
            </label>
            <label>
              Code postal <span className="req">*</span>
              <input
                name="code_postal"
                type="text"
                inputMode="numeric"
                pattern="\\d{4,6}"
                placeholder="Code postal*"
                required
              />
            </label>
          </div>

          <div className="contact-row">
            <label>
              NOM prénom <span className="req">*</span>
              <input name="nom_prenom" type="text" placeholder="NOM prénom" required />
            </label>
            <label>
              E-mail <span className="req">*</span>
              <input name="email" type="email" placeholder="E-mail*" required />
            </label>
          </div>

          <div className="contact-row">
            <label>
              Téléphone
              <input
                name="telephone"
                type="tel"
                placeholder="Téléphone"
                pattern="^[0-9+()\\s.-]{6,}$"
              />
            </label>
          </div>

          <label>
            Votre message
            <textarea name="message" rows="8" placeholder="Décrivez votre besoin…" />
          </label>

          <div className="contact-captcha">
            <input id="captcha" type="checkbox" required />
            <label htmlFor="captcha">Je ne suis pas un robot</label>
          </div>

          <button type="submit" className="contact-btn" disabled={status.state === "loading"}>
            {status.state === "loading" ? "ENVOI…" : "ENVOYER"}
          </button>

          {status.state === "ok" && <p className="contact-ok">{status.msg}</p>}
          {status.state === "error" && <p className="contact-error">{status.msg}</p>}
        </form>

        {/* === INFOS === */}
        <aside className="contact-aside">
          <h3 className="contact-subtitle">Nous situer</h3>
          <div className="contact-card">
            <p className="contact-site">VERATRAC SAFETY — Bureau Mohammedia</p>
            <p>Quartier du Parc, Mohammedia, Maroc</p>
            <p>
              <a href="tel:+212600000000">📞 +212 670 166 516</a>
              <br />
              <a href="mailto:contact.souda@veratrac.com"> Souda.Veratrac@gmail.com</a>
            </p>
          </div>

          <div className="contact-map">
            <iframe
              title="Localisation Mohammedia"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13161.195!2d-7.388!3d33.686!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda62d5c0b5b7f53%3A0x1f4c8a6d0b9e9a7c!2sMohammedia!5e0!3m2!1sfr!2sMA!4v0000000000"
            />
          </div>
        </aside>
      </div>
    </section>
  );
}
